/**
 * resolveDefeatChoice move + the defeat-with-a-Bystander eligible-target builder
 * and shared dispatcher (WP-486 / D-24291).
 *
 * Called by the current player after Silent Sniper's `defeat-with-bystander` hero
 * effect ("Defeat a Villain or Mastermind that has a Bystander.") found ≥2
 * eligible targets and parked a PendingDefeatChoice. The player picks which
 * Villain (by City space index) or the Mastermind to defeat for free; this move
 * validates the chosen target is in the parked eligible set, front-pops the
 * pending entry, THEN dispatches the defeat via the shared fight-defeat core.
 *
 * why (D-24291): the front-pop happens BEFORE the defeat dispatch so that a
 * villain's onFight ability that parks its OWN pending choice (KO-a-hero, scry-KO,
 * capture-bystander) lands BEHIND the now-removed defeat entry in FIFO order — the
 * defeat resolves first, its consequence resolves next, no board freeze.
 *
 * The defeat spends NO attack and sets NO acted-this-turn flag (Silent Sniper is a
 * card play, not a fight): the shared cores (defeatCityVillainCore /
 * defeatMastermindTacticCore) exclude both by construction.
 *
 * All invalid states are silent no-ops (moves never throw). The queue is left
 * byte-identical on every no-op so the player can resubmit a valid target — the
 * block-all guard guarantees the parked targets still exist while pending.
 *
 * No registry imports. No .reduce(). Moves never throw.
 */
import { isMastermindHaunting } from '../board/haunt.logic.js';
import { isFinalBlowAvailable } from '../mastermind/mastermind.logic.js';
import { defeatCityVillainCore } from './fightVillain.js';
import { awardMastermindOnFinalBlow, defeatMastermindTacticCore } from './fightMastermind.js';
/**
 * Whether any defeat-with-a-Bystander choice is currently pending.
 *
 * Single predicate imported by the block-all action-move guards. `undefined` and
 * `[]` both mean no pending choice (mirrors hasPendingReorderChoice, D-24286).
 *
 * @param G - The game state to inspect (not mutated).
 * @returns true when the pending-defeat queue holds at least one entry.
 */
export function hasPendingDefeatChoice(G) {
    return (G.pendingDefeatChoices?.length ?? 0) > 0;
}
/**
 * Whether a free-defeat effect (Silent Sniper, Pure Fury) may target the Mastermind.
 *
 * True when the Mastermind is not haunting (WP-757 / D-24587) and either a Tactic
 * remains (the free defeat defeats one Tactic) or, under the optional Final Blow rule,
 * the final blow is pending (D-24739 — the free defeat strikes it). A Mastermind that
 * is already vanquished is never a target.
 *
 * @param G - The game state to inspect (not mutated).
 * @returns true when the Mastermind is a valid free-defeat target.
 */
export function isMastermindFreeDefeatTarget(G) {
    if (isMastermindHaunting(G)) {
        return false;
    }
    if (G.mastermind.tacticsDeck.length > 0) {
        return true;
    }
    return isFinalBlowAvailable(G.mastermind, G.finalBlow);
}
/**
 * Builds the deterministic list of eligible `defeat-with-bystander` targets.
 *
 * A target is eligible when it holds ≥1 captured Bystander: a City Villain whose
 * ext_id has a non-empty `G.attachedBystanders` entry, or the Mastermind when
 * `G.mastermind.attachedBystanders` is non-empty AND at least one tactic remains
 * (an already-vanquished Mastermind cannot be re-defeated).
 *
 * why (D-24291): the order is pinned — City spaces ascending (NOT the
 * `attachedBystanders` map, whose key order is not a stable contract), Mastermind
 * appended last — because this exact order feeds BOTH the UIState projection and
 * the `ai.legalMoves` bot/sim default; a drift there would flip a seeded decision.
 *
 * @param G - The game state to inspect (not mutated).
 * @returns The eligible targets in the pinned order (City ascending, Mastermind last).
 */
export function buildDefeatWithBystanderTargets(G) {
    const targets = [];
    // why: iterate City spaces by ascending index (a stable contract) — NOT the
    // attachedBystanders map key order — so the target list replays identically.
    for (let cityIndex = 0; cityIndex < G.city.length; cityIndex++) {
        const cardId = G.city[cityIndex];
        if (cardId === null || cardId === undefined) {
            continue;
        }
        const attachedBystanders = G.attachedBystanders[cardId];
        if (attachedBystanders !== undefined && attachedBystanders.length > 0) {
            targets.push({ kind: 'villain', cityIndex, cardId });
        }
    }
    // why: the Mastermind's captured Bystanders live in a DIFFERENT store
    // (G.mastermind.attachedBystanders), so it is checked separately and appended
    // LAST. Require a remaining tactic — defeating "the Mastermind" defeats one
    // tactic — or, under Final Blow, a pending final blow (D-24739); a vanquished
    // Mastermind is not a defeatable target.
    // why: WP-757 / D-24587 — a haunting Mastermind can't be fought, so a free defeat
    // can't target it either (inside isMastermindFreeDefeatTarget).
    const mastermindBystanders = G.mastermind.attachedBystanders ?? [];
    if (mastermindBystanders.length > 0 && isMastermindFreeDefeatTarget(G)) {
        targets.push({ kind: 'mastermind', cardId: G.mastermind.baseCardId });
    }
    return targets;
}
/**
 * Builds the deterministic list of every City Villain as a free-defeat target —
 * Loki's "Cruel Ruler" Fight ("Defeat a Villain in the City for free") eligible
 * set (WP-693 / D-24510).
 *
 * Unlike buildDefeatWithBystanderTargets, a target is eligible simply for being a
 * villain/henchman occupying a City space — there is NO Bystander requirement and
 * NO Mastermind entry (the printed text is "a Villain in the City"). No Guard
 * filter: the shipped free defeat bypasses the Guard access restriction (it spends
 * no attack), and "for free" carries no such restriction — matching
 * buildDefeatWithBystanderTargets, which likewise does not check isGuardBlocking.
 *
 * why (D-24510): the order is pinned — City spaces ascending — because it feeds
 * BOTH the UIState projection and the bot/sim default (the first parked target),
 * exactly like buildDefeatWithBystanderTargets; a drift there would flip a seeded
 * decision. Each target is a `kind: 'villain'` DefeatWithBystanderTarget, so it
 * reuses the shared dispatchDefeatWithBystanderTarget path unchanged.
 *
 * @param G - The game state to inspect (not mutated).
 * @returns Every occupied City space as a villain free-defeat target, City ascending.
 */
export function buildCityVillainDefeatTargets(G) {
    const targets = [];
    // why: iterate City spaces by ascending index (a stable contract) so the target
    // list replays identically — the same pinned order buildDefeatWithBystanderTargets
    // uses. No .reduce() (effect/zone work per code-style rules).
    for (let cityIndex = 0; cityIndex < G.city.length; cityIndex++) {
        const cardId = G.city[cityIndex];
        if (cardId === null || cardId === undefined) {
            continue;
        }
        targets.push({ kind: 'villain', cityIndex, cardId });
    }
    return targets;
}
/**
 * Dispatches a chosen defeat-with-a-Bystander target through the shared
 * fight-defeat core — a City Villain via defeatCityVillainCore (fires the
 * villain's onFight abilities), or the Mastermind tactic via
 * defeatMastermindTacticCore (no onFight). Spends no attack (the cores exclude it).
 *
 * why (D-24291): both the exactly-1 auto-defeat path (heroEffectDefeatWithBystander)
 * and the ≥2 resolve path (resolveDefeatChoice) route through this one dispatcher,
 * so the free defeat can never drift between the two cardinalities.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param ctx - The bare boardgame.io ctx (currentPlayer + turn), typed unknown to
 *   avoid a framework import; forwarded to the shared cores.
 * @param target - The eligible target to defeat.
 * @param shuffleContext - ShuffleProvider ({ random }) for a villain Fight scry reshuffle.
 * @param events - The move's boardgame.io events (WP-694 / D-24511), forwarded to the
 *   mastermind tactic core so a parked multi-seat seat choice admits the non-active seats.
 *   Optional/guarded.
 */
export function dispatchDefeatWithBystanderTarget(G, ctx, target, shuffleContext, events) {
    if (target.kind === 'mastermind') {
        // why: D-24739 — under Final Blow with every Tactic gone, the free defeat strikes the
        // final blow through fightMastermind's own award (operator ruling 2026-10-17); the
        // tactic core would no-op on the empty deck.
        if (isFinalBlowAvailable(G.mastermind, G.finalBlow)) {
            awardMastermindOnFinalBlow(G, ctx);
            return;
        }
        // why: WP-694 / D-24511 — forward events so a Silent Sniper / Pure Fury free defeat of a
        // Monarch's Decree / Vanishing Illusions tactic can park a WP-684 multi-seat seat choice.
        defeatMastermindTacticCore(G, ctx, shuffleContext, events);
        return;
    }
    // why: a villain target always carries its City space index; guard defensively
    // (a malformed target with no index is a silent no-op rather than a bad read).
    if (typeof target.cityIndex === 'number') {
        defeatCityVillainCore(G, ctx, target.cityIndex, shuffleContext);
    }
}
/**
 * Finds the parked eligible target that matches the resolve payload, or undefined
 * when the payload names no parked target.
 *
 * @param targets - The parked eligible targets (the front pending entry snapshot).
 * @param args - The submitted { targetKind, cityIndex }.
 * @returns The matching parked target, or undefined.
 */
function findChosenTarget(targets, args) {
    for (const target of targets) {
        if (args.targetKind === 'mastermind' && target.kind === 'mastermind') {
            return target;
        }
        if (args.targetKind === 'villain' &&
            target.kind === 'villain' &&
            target.cityIndex === args.cityIndex) {
            return target;
        }
    }
    return undefined;
}
/**
 * Resolves the FRONT pending defeat-with-a-Bystander choice by defeating the
 * chosen target for free.
 *
 * Validate args → validate the front pending entry → the chosen target must be in
 * the parked eligible set → FRONT-POP the entry → dispatch the defeat via the
 * shared core. Silent no-ops: bad targetKind; a `villain` target with a
 * non-integer cityIndex; empty queue; front.playerID mismatch; front.choiceType
 * mismatch; a target not in the parked set (queue intact — resubmit).
 *
 * @param context - boardgame.io move context with G, ctx, playerID, random.
 * @param args - the chosen { targetKind, cityIndex }.
 */
export function resolveDefeatChoice({ G, ctx, playerID, random, events }, args) {
    // Step 1: Validate args
    // why: a client may submit the move with no payload (undefined) or null; reading a field off
    // either throws a TypeError, and moves never throw — reject before any field read.
    if (args === null || typeof args !== 'object') {
        return;
    }
    if (args.targetKind !== 'villain' && args.targetKind !== 'mastermind') {
        return;
    }
    if (args.targetKind === 'villain') {
        if (typeof args.cityIndex !== 'number' ||
            !Number.isInteger(args.cityIndex)) {
            return;
        }
    }
    // Step 2: Validate the front pending entry — front-only resolution (no index in
    // the payload, so a non-front entry can never be targeted)
    const queue = G.pendingDefeatChoices;
    if (queue === undefined || queue.length === 0) {
        return;
    }
    const front = queue[0];
    if (front.playerID !== playerID) {
        return;
    }
    // why: WP-682 / D-24499 — resolveDefeatChoice serves BOTH conditional free-defeat
    // families: Silent Sniper (defeat-with-bystander) and Nick Fury's Pure Fury
    // (pure-fury). Both park the same target-snapshot shape and dispatch through the same
    // shared free-defeat core, so either discriminant is a valid front entry here.
    // why: WP-693 / D-24510 — 'cruel-ruler' (Loki's Cruel Ruler) is the THIRD member of
    // this shared free-defeat family: it parks the same DefeatWithBystanderTarget snapshot
    // (built by buildCityVillainDefeatTargets) and dispatches through the same shared core,
    // so it is a valid front entry here alongside 'defeat-with-bystander' and 'pure-fury'.
    if (front.choiceType !== 'defeat-with-bystander' &&
        front.choiceType !== 'pure-fury' &&
        front.choiceType !== 'cruel-ruler') {
        return;
    }
    // Step 3: The chosen target must be one of the parked eligible targets. The
    // block-all guard froze the board since park time, so the snapshot still holds.
    const chosenTarget = findChosenTarget(front.targets, args);
    if (chosenTarget === undefined) {
        return;
    }
    // Step 4: FRONT-POP before the defeat dispatch — a villain's onFight ability can
    // park its OWN pending choice (KO-a-hero / scry-KO); popping the defeat entry
    // first makes that nested park land BEHIND it in FIFO order (D-24291), so the
    // defeat resolves before its consequence and the board never freezes.
    queue.shift();
    // Step 5: Dispatch the free defeat via the shared core (no attack spend). `ctx`
    // is the bare bgio ctx (currentPlayer); { random } supplies a villain Fight scry.
    // why: WP-694 / D-24511 — thread events so a free defeat of a multi-seat tactic parks its
    // WP-684 seat choice (setActivePlayers admits the non-active seats).
    dispatchDefeatWithBystanderTarget(G, ctx, chosenTarget, { random }, events);
}
//# sourceMappingURL=defeatChoice.resolve.js.map